import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IncentivesSummaryBox } from '../components/IncentivesSummaryBox';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type DistrictImpact = {
  propertyTax: number[];
  serviceCosts: number[];
  annualNetBenefits: number[];
  cumulativeNetBenefits: number[];
  presentValueContribution: number[];
  tenYearNetBenefits: number;
  tenYearPresentValue: number;
};

type AnalysisResults = {
  summary: {
    tenYearNetBenefits: number;
    tenYearPresentValue: number;
    grandTotalCityBenefit: number;
    totalIncentive: number;
    rateOfReturn: number | null;
    paybackPeriodYears: number | null;
    perJobIncentiveCost: number;
    perEntity?: {
      county?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
      isd?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
      special?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
    };
  };
  construction: {
    constructionJobs: number;
    totalConstructionCityRevenue: number;
  };
  projections: {
    stabilizedTotalJobs: number;
    stabilizedSpinOffJobs: number;
  };
  cityImpact: {
    annualNetBenefits: number[];
    cumulativeNetBenefits: number[];
    salesTaxProjectNetNew: number[];
    realPropertyTax: number[];
    bppPropertyTax: number[];
    hotelOccupancyTax: number[];
    utilityFranchiseFees: number[];
    miscTaxesAndFees: number[];
    costsSubtotal: number[];
    benefitsSubtotal: number[];
  };
  countyImpact?: DistrictImpact;
  isdImpact?: DistrictImpact;
  specialDistrictImpact?: DistrictImpact;
};

type ValidationBypassRecord = {
  field: string;
  metric: string;
  severity: string;
  analystValue: number;
  derivedValue: number;
  derivedLabel: string;
  justification: string;
};

type Analysis = {
  id: string;
  projectName: string;
  status: string;
  createdAt: string;
  inputs:
    | ({
        _validationBypasses?: { bypasses: ValidationBypassRecord[]; validatedAt: string };
      } & Record<string, unknown>)
    | null;
  results: AnalysisResults | null;
};

function fmt(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function fmtNum(n: number, d = 1) {
  return n.toLocaleString('en-US', { maximumFractionDigits: d });
}

/**
 * Formats a payback period for display. When an entity has zero abatement
 * cost, the engine reports paybackPeriodYears = 0 with rateOfReturn = null,
 * which is not a real "instant payback" and must not render as "0 yrs".
 */
function fmtPaybackYears(years: number | null, rateOfReturn: number | null) {
  if (rateOfReturn === null && years === 0) return 'N/A';
  return years !== null ? `${fmtNum(years)} yrs` : 'N/A';
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded border border-slate-200 p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}

/**
 * Year-by-year district table (county / ISD / special). Mirrors the on-screen
 * DistrictImpactTable from AnalysisResultRoute but rendered as a compact
 * print-friendly table with year columns across the top.
 */
function DistrictDetailTable({
  label,
  impact,
  entityMetrics,
}: {
  label: string;
  impact: DistrictImpact;
  entityMetrics?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
}) {
  const years = impact.annualNetBenefits.length;
  return (
    <>
      <h2 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
        {entityMetrics && (
          <span className="ml-2 font-normal normal-case text-slate-400">
            · Payback:{' '}
            {fmtPaybackYears(entityMetrics.paybackPeriodYears, entityMetrics.rateOfReturn)} · ROI:{' '}
            {entityMetrics.rateOfReturn !== null
              ? `${(entityMetrics.rateOfReturn * 100).toFixed(1)}%`
              : 'N/A'}
          </span>
        )}
      </h2>
      <div className="mb-6 overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">Category</th>
              {Array.from({ length: years }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: year columns are positional
                <th key={i} className="px-3 py-2 text-right font-semibold text-slate-600">
                  Yr {i + 1}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-semibold text-slate-600">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              { label: 'Property Tax', arr: impact.propertyTax },
              { label: 'Service Costs', arr: impact.serviceCosts.map((v) => -v) },
              { label: 'Net Annual Benefit', arr: impact.annualNetBenefits, bold: true },
              { label: 'Cumulative Net', arr: impact.cumulativeNetBenefits, bold: true },
            ].map(({ label: rowLabel, arr, bold }) => (
              <tr key={rowLabel} className={bold ? 'bg-slate-50 font-semibold' : ''}>
                <td className="px-3 py-1.5 text-slate-700">{rowLabel}</td>
                {arr.map((v, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: year columns are positional
                  <td key={i} className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                    {fmt(v)}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-700">
                  {fmt(arr.reduce((a, b) => a + b, 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Minimal print-only layout. Triggered by browser print / Playwright. */
export function PrintAnalysisRoute() {
  const { id } = useParams<{ id: string }>();
  const getToken = useSessionTokenGetter();
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = await res.json();
        if (!cancelled) setAnalysis(json.analysis as Analysis);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  if (error || !analysis) {
    return <p className="p-8 text-sm text-red-600">{error ?? 'Analysis not found.'}</p>;
  }

  const r = analysis.results;
  if (!r) {
    return <p className="p-8 text-sm text-slate-500">This analysis has not been calculated yet.</p>;
  }

  const years = r.cityImpact.annualNetBenefits.length;
  const validationReport = analysis.inputs?._validationBypasses ?? null;

  return (
    <div className="w-full px-6 py-8 font-sans text-slate-900 print:p-0 sm:px-10 lg:px-14">
      {/* Cover */}
      <div className="mb-8 border-b-2 border-slate-900 pb-6">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Develio — Fiscal Impact Analysis
        </p>
        <h1 className="text-3xl font-bold">{analysis.projectName}</h1>
        <p className="mt-2 text-sm text-slate-500">
          Generated{' '}
          {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}{' '}
          · Status: <span className="capitalize">{analysis.status.replace(/_/g, ' ')}</span>
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          className="mt-4 rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 print:hidden"
        >
          Print / Save as PDF
        </button>
      </div>

      {/* Incentive package — full-width, mirrors on-screen IncentivesSummaryBox.
          Uses the shared summarizeIncentivePackage helper so the levers shown
          here agree with the on-screen analysis page and PDFs. Renders nothing
          when the package is empty. */}
      <div className="mb-8">
        <IncentivesSummaryBox
          inputs={analysis.inputs}
          summary={{
            totalIncentive: r.summary.totalIncentive,
            perJobCost: r.summary.perJobIncentiveCost,
            rateOfReturn: r.summary.rateOfReturn,
            paybackYears: r.summary.paybackPeriodYears,
          }}
        />
      </div>

      {/* 10-Year Summary KPIs */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        10-Year Fiscal Summary
      </h2>
      <div className="mb-8 grid grid-cols-4 gap-3">
        <KpiCard
          label="Net City Benefit (10 yr)"
          value={fmt(r.summary.tenYearNetBenefits)}
          sub="Nominal"
        />
        <KpiCard
          label="Present Value (10 yr)"
          value={fmt(r.summary.tenYearPresentValue)}
          sub="Discounted"
        />
        <KpiCard
          label="Grand Total City Benefit"
          value={fmt(r.summary.grandTotalCityBenefit)}
          sub="Ops + construction"
        />
        <KpiCard
          label="Payback Period"
          value={
            r.summary.paybackPeriodYears !== null
              ? `${fmtNum(r.summary.paybackPeriodYears)} yrs`
              : 'N/A'
          }
        />
      </div>

      {/* Employment */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Employment Impact
      </h2>
      <div className="mb-8 grid grid-cols-4 gap-3">
        <KpiCard
          label="Stabilized Total Jobs"
          value={fmtNum(r.projections.stabilizedTotalJobs, 0)}
          sub="Direct + spin-off"
        />
        <KpiCard
          label="Spin-Off Jobs"
          value={fmtNum(r.projections.stabilizedSpinOffJobs, 0)}
          sub="Indirect + induced"
        />
        <KpiCard
          label="Construction Jobs"
          value={fmtNum(r.construction.constructionJobs, 0)}
          sub="Total supported"
        />
        <KpiCard
          label="Cost Per Job"
          value={r.summary.perJobIncentiveCost > 0 ? fmt(r.summary.perJobIncentiveCost) : '—'}
          sub="Incentive ÷ stabilized jobs"
        />
      </div>

      {/* Incentive */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Incentive Analysis
      </h2>
      <div className="mb-8 grid grid-cols-3 gap-3">
        <KpiCard label="Total Incentive Cost" value={fmt(r.summary.totalIncentive)} />
        <KpiCard
          label="Rate of Return"
          value={
            r.summary.rateOfReturn !== null
              ? `${(r.summary.rateOfReturn * 100).toFixed(1)}%`
              : 'N/A'
          }
        />
        <KpiCard
          label="Construction City Revenue"
          value={fmt(r.construction.totalConstructionCityRevenue)}
        />
      </div>

      {/* 10-Year Fiscal Detail Table */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        10-Year Fiscal Detail
      </h2>
      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">Category</th>
              {Array.from({ length: years }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: year columns are positional, stable order
                <th key={i} className="px-3 py-2 text-right font-semibold text-slate-600">
                  Yr {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              { label: 'Sales Tax', arr: r.cityImpact.salesTaxProjectNetNew },
              { label: 'Real Property Tax', arr: r.cityImpact.realPropertyTax },
              { label: 'BPP Property Tax', arr: r.cityImpact.bppPropertyTax },
              { label: 'Hotel Occupancy Tax', arr: r.cityImpact.hotelOccupancyTax },
              { label: 'Utility / Franchise Fees', arr: r.cityImpact.utilityFranchiseFees },
              { label: 'Misc Taxes & Fees', arr: r.cityImpact.miscTaxesAndFees },
              { label: 'Benefits Subtotal', arr: r.cityImpact.benefitsSubtotal, bold: true },
              {
                label: 'Cost of Services',
                arr: r.cityImpact.costsSubtotal.map((v) => -v),
              },
              { label: 'Net Annual Benefit', arr: r.cityImpact.annualNetBenefits, bold: true },
              {
                label: 'Cumulative Net Benefit',
                arr: r.cityImpact.cumulativeNetBenefits,
                bold: true,
              },
            ].map(({ label, arr, bold }) => (
              <tr key={label} className={bold ? 'bg-slate-50 font-semibold' : ''}>
                <td className="px-3 py-1.5 text-slate-700">{label}</td>
                {arr.map((v, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: year columns are positional, stable order
                  <td key={i} className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                    {fmt(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Multi-District Summary — shows any additional taxing jurisdictions
          (county, ISD, special district) that the analysis touched. */}
      {(r.countyImpact || r.isdImpact || r.specialDistrictImpact) && (
        <>
          <h2 className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Multi-District Summary (10 yr Net)
          </h2>
          <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {r.countyImpact && (
              <KpiCard
                label="County"
                value={fmt(r.countyImpact.tenYearNetBenefits)}
                sub="Property tax − services"
              />
            )}
            {r.isdImpact && (
              <KpiCard
                label="ISD"
                value={fmt(r.isdImpact.tenYearNetBenefits)}
                sub="Property tax − student costs"
              />
            )}
            {r.specialDistrictImpact && (
              <KpiCard
                label="Special District"
                value={fmt(r.specialDistrictImpact.tenYearNetBenefits)}
                sub="Property tax − operating costs"
              />
            )}
            <KpiCard
              label="All Districts Combined"
              value={fmt(
                r.summary.tenYearNetBenefits +
                  (r.countyImpact?.tenYearNetBenefits ?? 0) +
                  (r.isdImpact?.tenYearNetBenefits ?? 0) +
                  (r.specialDistrictImpact?.tenYearNetBenefits ?? 0),
              )}
              sub="City + county + ISD + special"
            />
          </div>

          {r.countyImpact && (
            <DistrictDetailTable
              label="County Fiscal Detail"
              impact={r.countyImpact}
              entityMetrics={r.summary.perEntity?.county}
            />
          )}
          {r.isdImpact && (
            <DistrictDetailTable
              label="ISD Fiscal Detail"
              impact={r.isdImpact}
              entityMetrics={r.summary.perEntity?.isd}
            />
          )}
          {r.specialDistrictImpact && (
            <DistrictDetailTable
              label="Special District Fiscal Detail"
              impact={r.specialDistrictImpact}
              entityMetrics={r.summary.perEntity?.special}
            />
          )}
        </>
      )}

      {/* Validation Report Appendix */}
      {validationReport && validationReport.bypasses.length > 0 && (
        <div className="mt-10 break-before-page">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Appendix — Validation Report
          </h2>
          <p className="mb-4 text-xs text-slate-500">
            The following benchmark flags were reviewed and acknowledged by the analyst prior to
            submission on{' '}
            {new Date(validationReport.validatedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            .
          </p>
          <div className="space-y-4">
            {validationReport.bypasses.map((b) => (
              <div key={b.field} className="rounded border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      b.severity === 'warning'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {b.severity}
                  </span>
                  <span className="text-sm font-semibold text-slate-700">{b.metric}</span>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Analyst value: {fmt(b.analystValue)} → {fmt(b.derivedValue)} {b.derivedLabel}
                </p>
                <div className="rounded bg-slate-50 px-3 py-2">
                  <p className="text-[10px] font-semibold text-slate-500 mb-0.5 uppercase tracking-wide">
                    Analyst Justification
                  </p>
                  <p className="text-xs text-slate-700">{b.justification}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-10 flex justify-between border-t border-slate-200 pt-4 text-[10px] text-slate-400">
        <span>Develio — Confidential</span>
        <span>Develio</span>
      </div>
    </div>
  );
}
